import React, { ReactNode } from 'react';
import { Box, Paper, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

interface BookPageProps {
  children: ReactNode;
  pageNumber?: number;
  isLeftPage?: boolean;
  showPageNumber?: boolean;
  backgroundColor?: string;
  paperTexture?: boolean;
}

const BookPage: React.FC<BookPageProps> = ({
  children,
  pageNumber,
  isLeftPage = false,
  showPageNumber = true,
  backgroundColor,
  paperTexture = true,
}) => {
  const theme = useTheme();
  
  const pageVariants = {
    enter: (isLeftPage: boolean) => ({
      rotateY: isLeftPage ? -180 : 180,
      opacity: 0,
      scale: 0.8,
    }),
    center: {
      rotateY: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (isLeftPage: boolean) => ({
      rotateY: isLeftPage ? 180 : -180,
      opacity: 0,
      scale: 0.8,
    }),
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        custom={isLeftPage}
        variants={pageVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{
          duration: 0.6,
          ease: [0.43, 0.13, 0.23, 0.96],
        }}
        style={{
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
          perspective: '1000px',
        }}
      >
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: backgroundColor || (theme.palette.mode === 'dark' 
              ? 'rgba(46, 33, 23, 0.95)' 
              : '#FFFEF8'),
            backgroundImage: paperTexture ? `
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 2px,
                rgba(139, 69, 19, 0.03) 2px,
                rgba(139, 69, 19, 0.03) 4px
              ),
              repeating-linear-gradient(
                0deg,
                transparent,
                transparent 2px,
                rgba(139, 69, 19, 0.02) 2px,
                rgba(139, 69, 19, 0.02) 4px
              ),
              radial-gradient(
                ellipse at top left,
                rgba(255, 248, 240, 0.4) 0%,
                transparent 50%
              ),
              radial-gradient(
                ellipse at bottom right,
                rgba(139, 69, 19, 0.05) 0%,
                transparent 50%
              )
            ` : 'none',
            overflow: 'hidden',
            borderRadius: isLeftPage ? '8px 2px 2px 8px' : '2px 8px 8px 2px',
            boxShadow: isLeftPage 
              ? 'inset 3px 0 7px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.12)' 
              : 'inset -3px 0 7px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.12)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: isLeftPage ? 'auto' : 0,
              right: isLeftPage ? 0 : 'auto',
              width: '30px',
              height: '100%',
              background: `linear-gradient(${isLeftPage ? '270deg' : '90deg'}, 
                rgba(0,0,0,0.05) 0%, 
                transparent 100%)`,
              pointerEvents: 'none',
            },
            '&::after': paperTexture ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(139, 69, 19, 0.02) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(139, 69, 19, 0.02) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(255, 235, 205, 0.03) 0%, transparent 50%)
              `,
              pointerEvents: 'none',
            } : {},
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              p: { xs: 2, sm: 3, md: 4 },
              position: 'relative',
            }}
          >
            {children}
            
            {/* Page Number */}
            {showPageNumber && pageNumber && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: isLeftPage ? 24 : 'auto',
                  right: isLeftPage ? 'auto' : 24,
                  fontFamily: '"Crimson Text", serif',
                  fontSize: '14px',
                  color: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 248, 240, 0.5)' 
                    : 'rgba(139, 69, 19, 0.6)',
                  fontStyle: 'italic',
                }}
              >
                {pageNumber}
              </Box>
            )}
            
            {/* Page Edge Shadow */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: isLeftPage ? 'auto' : -2,
                right: isLeftPage ? -2 : 'auto',
                width: 4,
                background: 'linear-gradient(90deg, rgba(0,0,0,0.2) 0%, transparent 100%)',
                filter: 'blur(2px)',
              }}
            />
          </Box>
        </Paper>
      </motion.div>
    </AnimatePresence>
  );
};

export default React.memo(BookPage);